/**
 * WordClaimInput - fast word entry interface
 * Following UX advisor recommendations for speed and clarity
 */

import { useState, useRef, useEffect, FormEvent, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { socketActions } from '../../lib/socketActions';
import { useGameStore } from '../../stores/gameStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { triggerHaptic } from '../../utils/haptics';
import { hasFinePointer } from '../../utils/device';
import { CLAIM_INPUT_ATTR } from '../../hooks/useKeyboardShortcuts';
import clsx from 'clsx';
import type { ClaimedWord } from '../../types/generated';

const EMPTY_MIDDLE: string[] = [];
const EMPTY_PLAYER_WORDS: Record<string, ClaimedWord[]> = {};
const EMPTY_SET = new Set<number>();
const { setWordValidation, setTypingHighlightIndices, clearWordsToSteal } = useGameStore.getState();

// Debounce function with cancel support for cleanup
type DebouncedFunction<T extends (...args: Parameters<T>) => void> = ((...args: Parameters<T>) => void) & { cancel: () => void };

function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): DebouncedFunction<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn(...args);
    }, delay);
  };
  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  return debounced as DebouncedFunction<T>;
}

interface LetterStatus {
  letter: string;
  available: boolean;
  source: 'middle' | 'stolen' | 'potential' | 'missing';
}

export function WordClaimInput() {
  const [word, setWord] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const claimStatus = useGameStore((s) => s.claimStatus);
  const claimError = useGameStore((s) => s.claimError);
  const status = useGameStore((s) => s.gameState?.status);
  const middle = useGameStore((s) => s.gameState?.middle ?? EMPTY_MIDDLE);
  const playerWords = useGameStore((s) => s.gameState?.playerWords ?? EMPTY_PLAYER_WORDS);
  const selectedWordsToSteal = useGameStore((s) => s.selectedWordsToSteal);
  const wordValidation = useGameStore((s) => s.wordValidation);
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);
  const [finePointer] = useState(hasFinePointer);

  // Create debounced lookup function with ref to ensure cleanup on dependency changes
  const debouncedLookupRef = useRef<DebouncedFunction<(w: string) => void> | null>(null);

  // Recreate debounced function when dependencies change, cancelling old one first
  const debouncedLookup = useMemo(() => {
    // Cancel any pending call from previous debounced function
    debouncedLookupRef.current?.cancel();

    const newDebounced = debounce((w: string) => {
      if (w.length >= 4) {
        // Mark as checking before sending
        setWordValidation({ word: w.toUpperCase(), isValid: null });
        socketActions.validateWord(w);
      }
    }, 300);

    debouncedLookupRef.current = newDebounced;
    return newDebounced;
  }, []);

  // Trigger validation as user types (for 4+ letters)
  // Cleanup pending debounced calls on unmount
  useEffect(() => {
    const upperWord = word.toUpperCase();
    if (upperWord.length >= 4) {
      debouncedLookup(upperWord);
    } else {
      // Clear validation for short words
      setWordValidation(null);
    }
    // Cleanup: cancel pending debounced lookup on word change or unmount
    return () => debouncedLookup.cancel();
  }, [word, debouncedLookup]);

  // Check if word validation matches current input
  const validationMatchesInput = wordValidation?.word === word.toUpperCase();
  const isChecking = validationMatchesInput && wordValidation?.isValid === null;
  const isWordValid = validationMatchesInput && wordValidation?.isValid === true;
  const isWordInvalid = validationMatchesInput && wordValidation?.isValid === false;

  // Create stable key from selected words Set for reliable dependency tracking
  // Sets don't compare well in React's useMemo dependency array
  const selectedWordsKey = useMemo(
    () => Array.from(selectedWordsToSteal).sort().join(','),
    [selectedWordsToSteal]
  );

  // Validate word letters availability and track which middle tiles are being used
  const { letterStatuses, usedMiddleIndices, unusedStolenLetters } = useMemo((): {
    letterStatuses: LetterStatus[],
    usedMiddleIndices: Set<number>,
    unusedStolenLetters: string[]  // Letters from stolen word(s) not used in new word
  } => {
    if (!status || word.length === 0) {
      return { letterStatuses: [], usedMiddleIndices: new Set(), unusedStolenLetters: [] };
    }

    const upperWord = word.toUpperCase();
    const isStealMode = selectedWordsToSteal.size > 0;

    // Build pool of available letters from middle, tracking indices for each letter
    // Map from letter -> list of available indices for that letter
    const middleIndicesByLetter: Map<string, number[]> = new Map();
    middle.forEach((letter, index) => {
      const indices = middleIndicesByLetter.get(letter) || [];
      indices.push(index);
      middleIndicesByLetter.set(letter, indices);
    });

    // Add letters from selected stolen words
    const stolenPool: Map<string, number> = new Map();
    const totalStolenLetters: string[] = [];
    for (const wordId of selectedWordsToSteal) {
      // Find the word in any player's words
      for (const words of Object.values(playerWords)) {
        const stolenWord = words.find(w => w.id === wordId);
        if (stolenWord) {
          for (const letter of stolenWord.letters) {
            stolenPool.set(letter, (stolenPool.get(letter) || 0) + 1);
            totalStolenLetters.push(letter);
          }
        }
      }
    }

    // Build pool of ALL letters from ALL player words (for "potential" steals)
    const potentialPool: Map<string, number> = new Map();
    for (const words of Object.values(playerWords)) {
      for (const playerWord of words) {
        // Don't count already-selected words in potential pool
        if (!selectedWordsToSteal.has(playerWord.id)) {
          for (const letter of playerWord.letters) {
            potentialPool.set(letter, (potentialPool.get(letter) || 0) + 1);
          }
        }
      }
    }

    // Check each letter in the typed word
    // Track how many of each letter we've used from each pool
    const usedMiddleCountByLetter: Map<string, number> = new Map();
    const usedFromStolen: Map<string, number> = new Map();
    const usedFromPotential: Map<string, number> = new Map();
    const usedIndices: Set<number> = new Set();

    const statuses = Array.from(upperWord).map((letter) => {
      // PRIORITY CHANGES BASED ON MODE:
      // - Steal mode: stolen → middle → potential → missing
      // - Normal mode: middle → stolen → potential → missing

      if (isStealMode) {
        // STEAL MODE: Try stolen pool FIRST
        const stolenAvail = (stolenPool.get(letter) || 0) - (usedFromStolen.get(letter) || 0);
        if (stolenAvail > 0) {
          usedFromStolen.set(letter, (usedFromStolen.get(letter) || 0) + 1);
          return { letter, available: true, source: 'stolen' as const };
        }

        // Then try middle pool
        const indices = middleIndicesByLetter.get(letter) || [];
        const usedCount = usedMiddleCountByLetter.get(letter) || 0;
        if (usedCount < indices.length) {
          usedIndices.add(indices[usedCount]);
          usedMiddleCountByLetter.set(letter, usedCount + 1);
          return { letter, available: true, source: 'middle' as const };
        }
      } else {
        // NORMAL MODE: Try middle pool first
        const indices = middleIndicesByLetter.get(letter) || [];
        const usedCount = usedMiddleCountByLetter.get(letter) || 0;
        if (usedCount < indices.length) {
          usedIndices.add(indices[usedCount]);
          usedMiddleCountByLetter.set(letter, usedCount + 1);
          return { letter, available: true, source: 'middle' as const };
        }

        // Then try stolen pool (selected words)
        const stolenAvail = (stolenPool.get(letter) || 0) - (usedFromStolen.get(letter) || 0);
        if (stolenAvail > 0) {
          usedFromStolen.set(letter, (usedFromStolen.get(letter) || 0) + 1);
          return { letter, available: true, source: 'stolen' as const };
        }
      }

      // Then try potential pool (could steal from any word)
      const potentialAvail = (potentialPool.get(letter) || 0) - (usedFromPotential.get(letter) || 0);
      if (potentialAvail > 0) {
        usedFromPotential.set(letter, (usedFromPotential.get(letter) || 0) + 1);
        return { letter, available: true, source: 'potential' as const };
      }

      return { letter, available: false, source: 'missing' as const };
    });

    // Calculate unused letters from stolen word(s)
    // Start with all stolen letters, remove the ones we used
    const unusedLetters: string[] = [...totalStolenLetters];
    for (const [letter, count] of usedFromStolen) {
      for (let i = 0; i < count; i++) {
        const idx = unusedLetters.indexOf(letter);
        if (idx !== -1) unusedLetters.splice(idx, 1);
      }
    }

    return { letterStatuses: statuses, usedMiddleIndices: usedIndices, unusedStolenLetters: unusedLetters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, status, middle, playerWords, selectedWordsKey, selectedWordsToSteal]);

  // Highlight the middle tiles used by the current input (store skips no-op updates)
  useEffect(() => {
    setTypingHighlightIndices(word.length > 0 ? usedMiddleIndices : EMPTY_SET);
  }, [word, usedMiddleIndices]);

  // Clear highlights when component unmounts (prevents stale highlights when leaving game)
  useEffect(() => {
    return () => setTypingHighlightIndices(new Set());
  }, []);

  const allLettersAvailable = letterStatuses.length >= 4 && letterStatuses.every(s => s.available);
  const hasAnyMissing = letterStatuses.some(s => !s.available);
  // Check if any letters could come from words that aren't selected yet
  const hasPotentialLetters = letterStatuses.some(s => s.source === 'potential');
  // Check if claim is ready to submit (all letters from middle OR selected stolen words, not potential)
  const isClaimReady = letterStatuses.length >= 4 &&
    letterStatuses.every(s => s.available && s.source !== 'potential');

  const isGameActive = status === 'playing';
  const hasSelectedWords = selectedWordsToSteal.size > 0;

  // Keep input focused when game is active - focus on any click or keypress
  useEffect(() => {
    if (!isGameActive) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't steal focus from other controls, dialogs or the chat panel
      if (!target.closest('input, button, textarea, select, [role="dialog"], [data-no-claim-focus]')) {
        inputRef.current?.focus();
      }
    };

    // Auto-focus on any letter keypress so user can just start typing
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      // Don't intercept if already in an input
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      // Don't intercept if in a dialog or the chat panel
      if (target.closest('[role="dialog"], [data-no-claim-focus]')) return;
      // Don't intercept special keys
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Only intercept letter keys (a-z, A-Z)
      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        inputRef.current?.focus();
        // Append the typed letter to the input
        setWord((prev) => (prev + e.key).toUpperCase());
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isGameActive]);

  // Focus input when game starts
  useEffect(() => {
    if (isGameActive) {
      inputRef.current?.focus();
    }
  }, [isGameActive]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedWord = word.trim().toUpperCase();
    if (!trimmedWord || trimmedWord.length < 4 || claimStatus === 'claiming') {
      return;
    }

    triggerHaptic('light');

    // Always pass stolen word IDs when user has words selected
    // The server will validate which letters come from which source
    // This fixes the bug where letters in both middle AND stolen words were only marked as 'middle'
    const stolenIds = selectedWordsToSteal.size > 0 ? Array.from(selectedWordsToSteal) : undefined;
    socketActions.claimWord(trimmedWord, stolenIds);

    // Clear input, selections, validation, and highlights on submit (optimistically)
    setWord('');
    clearWordsToSteal();
    setWordValidation(null);
    setTypingHighlightIndices(EMPTY_SET);
  };

  const getStatusClass = () => {
    switch (claimStatus) {
      case 'claiming':
        return hasSelectedWords ? 'bg-orange-500' : 'bg-blue-500';
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return hasSelectedWords
          ? 'bg-orange-500 hover:bg-orange-600'
          : 'bg-blue-500 hover:bg-blue-600';
    }
  };

  const getStatusText = () => {
    switch (claimStatus) {
      case 'claiming':
        return hasSelectedWords ? 'Stealing...' : 'Claiming...';
      case 'success':
        return hasSelectedWords ? 'Stolen!' : 'Claimed!';
      case 'error':
        return 'Failed';
      default:
        return hasSelectedWords ? 'STEAL' : 'CLAIM';
    }
  };

  if (!isGameActive) return null;

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={word}
          onChange={(e) => setWord(e.target.value.toUpperCase())}
          placeholder={hasSelectedWords ? 'Type new word to steal...' : 'Type word and press Enter'}
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          {...{ [CLAIM_INPUT_ATTR]: '' }}
          className={clsx(
            'claim-input flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-lg text-lg font-mono outline-none transition-all bg-white dark:bg-slate-800 text-slate-900 dark:text-white',
            hasSelectedWords
              ? 'border-orange-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-200'
              : 'border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
          )}
          // Never `disabled` while claiming: that blurs the field and closes the
          // mobile keyboard. Submission is guarded in handleSubmit instead.
          aria-busy={claimStatus === 'claiming'}
          aria-label={hasSelectedWords ? 'Type a new word to steal' : 'Type a word to claim'}
          onKeyDown={(e) => {
            // Esc clears a half-typed word first; only an empty field lets Esc pause
            if (e.key === 'Escape' && word) {
              e.preventDefault();
              setWord('');
            }
          }}
        />
        <motion.button
          type="submit"
          disabled={!word.trim() || word.length < 4 || claimStatus === 'claiming' || (word.length >= 4 && !isClaimReady)}
          className={clsx(
            'touch-target shrink-0 px-4 sm:px-6 py-2 sm:py-3 font-bold text-white rounded-lg transition-colors',
            getStatusClass(),
            (!word.trim() || word.length < 4 || claimStatus === 'claiming' || (word.length >= 4 && !isClaimReady)) &&
              'opacity-50 cursor-not-allowed'
          )}
          whileTap={!reducedMotion ? { scale: 0.95 } : undefined}
        >
          {getStatusText()}
        </motion.button>
      </form>

      {/* Error message - ARIA live for screen readers */}
      <AnimatePresence>
        {claimStatus === 'error' && claimError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-2 text-red-600 text-sm"
            role="alert"
            aria-live="assertive"
          >
            {claimError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Word preview with letter availability */}
      <AnimatePresence>
        {word.length > 0 && letterStatuses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 overflow-hidden"
          >
            <div className="flex items-center gap-1 justify-center flex-wrap">
              {letterStatuses.map((status, idx) => (
                <motion.span
                  key={idx}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: idx * 0.02 }}
                  className={clsx(
                    'w-7 h-8 flex items-center justify-center font-mono font-bold text-sm rounded border-2',
                    // In steal mode: stolen=green (primary), middle=blue (added)
                    // In normal mode: middle=green (primary)
                    status.available && status.source === 'stolen' && 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300',
                    status.available && status.source === 'middle' && (hasSelectedWords
                      ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                      : 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300'),
                    status.available && status.source === 'potential' && 'bg-yellow-100 dark:bg-yellow-900/40 border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300',
                    !status.available && 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600 text-red-500 dark:text-red-400 line-through'
                  )}
                  title={
                    status.available
                      ? status.source === 'middle' ? (hasSelectedWords ? 'Added from middle' : 'Available from middle')
                        : status.source === 'stolen' ? 'From stolen word'
                        : 'Click a word to steal from'
                      : 'Letter not available'
                  }
                >
                  {status.letter}
                </motion.span>
              ))}
            </div>
            {/* Show unused letters from stolen word(s) */}
            {hasSelectedWords && unusedStolenLetters.length > 0 && (
              <div className="text-center mt-1 text-xs text-red-500 dark:text-red-400">
                Unused from stolen word: <span className="font-mono font-bold">{unusedStolenLetters.join(' ')}</span>
                <span className="text-slate-500 dark:text-slate-400 ml-1">(must use all letters)</span>
              </div>
            )}
            {/* Status message with word validity - PRIORITY: invalid word > missing letters > potential steals */}
            <div className="text-center mt-1 flex items-center justify-center gap-2">
              {word.length < 4 ? (
                <span className="text-xs text-slate-400">Need {4 - word.length} more letter{4 - word.length !== 1 ? 's' : ''}</span>
              ) : isWordInvalid ? (
                // PRIORITY 1: Show invalid word error first (before missing letters)
                <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Not a valid word
                </span>
              ) : hasAnyMissing ? (
                // PRIORITY 2: Missing letters (only if word might be valid)
                <span className="text-xs text-red-500">Missing letters highlighted in red</span>
              ) : hasPotentialLetters ? (
                // PRIORITY 3: Has potential letters - needs to select source word(s)
                <span className="text-xs text-yellow-600">Click a word to steal letters from (yellow tiles)</span>
              ) : isChecking ? (
                // Checking dictionary...
                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Checking...
                </span>
              ) : isWordValid ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Valid word - press Enter!
                </span>
              ) : allLettersAvailable ? (
                <span className="text-xs text-green-600">All letters available - press Enter!</span>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyboard hint for keyboard/mouse devices */}
      {finePointer && (
        <div className="hidden md:block mt-2 text-xs text-slate-400 text-center">
          Enter to claim • Click words to steal • Arrow keys to flip
        </div>
      )}

      {/* Color legend - show when typing (hidden on short landscape screens) */}
      {word.length > 0 && (
        <div className="claim-legend mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
          {hasSelectedWords ? (
            // Steal mode legend
            <>
              <span><span className="inline-block w-2 h-2 rounded bg-green-400 mr-1" />from stolen</span>
              <span><span className="inline-block w-2 h-2 rounded bg-blue-400 mr-1" />added from middle</span>
              <span><span className="inline-block w-2 h-2 rounded bg-yellow-400 mr-1" />need to select</span>
              <span><span className="inline-block w-2 h-2 rounded bg-red-400 mr-1" />missing</span>
            </>
          ) : (
            // Normal mode legend
            <>
              <span><span className="inline-block w-2 h-2 rounded bg-green-400 mr-1" />from middle</span>
              <span><span className="inline-block w-2 h-2 rounded bg-yellow-400 mr-1" />can steal</span>
              <span><span className="inline-block w-2 h-2 rounded bg-red-400 mr-1" />missing</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
